import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Flashcard from './Flashcard';
import './FlashcardsPage.css';
import { getWordKey, getGroupId, persistSm2SrsStateToSupabase, srsSnapshotToWordFields } from '../utils/srs';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabaseClient';
import { useData } from '../contexts/DataContext';



const FlashcardsPage = () => {
  const { t, isRTL } = useLanguage();
  const { id, groupId } = useParams();
  const { modules } = useData();

  const navigate = useNavigate();
  const location = useLocation();

  // Get user session
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const getSession = async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      setSession(currentSession);
    };
    getSession();
  }, []);

  // Get mode from URL search params or location state
  const searchParams = new URLSearchParams(location.search);
  const modeFromUrl = searchParams.get('mode');
  const { mode: modeFromState, groupWords } = location.state || {};
  const mode = modeFromUrl || modeFromState;
  const currentMode = mode || 'classic';

  const [moduleData, setModuleData] = useState(null);
  const [groupData, setGroupData] = useState(null);
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);



  const useLearningUI = mode === 'learning' || mode === 'sm2';
  const useLearningData = mode === 'learning' || mode === 'classic';


  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [activeWords, setActiveWords] = useState([]);
  const [learningMode, setLearningMode] = useState(false);
  const [preloadedSRSData, setPreloadedSRSData] = useState({});
  const requestIdRef = useRef(0);
  const initialCardSelectedRef = useRef(false);
  const [loadingSRS, setLoadingSRS] = useState(true);
  const [srsDataReady, setSrsDataReady] = useState(false);
  const [sm2LastUndo, setSm2LastUndo] = useState(null);
  const [sm2UndoError, setSm2UndoError] = useState('');
  const [learningLastUndo, setLearningLastUndo] = useState(null);
  const [learningUndoError, setLearningUndoError] = useState('');
  /** Bumps Flashcard remount after learning undo so SRS hook reloads when index+wordId are unchanged (e.g. single-word group). */
  const [learningCardEpoch, setLearningCardEpoch] = useState(0);

  useEffect(() => {
    if (currentMode !== 'sm2') {
      setSm2LastUndo(null);
      setSm2UndoError('');
    }
  }, [currentMode]);

  useEffect(() => {
    if (currentMode !== 'learning') {
      setLearningLastUndo(null);
      setLearningUndoError('');
    }
  }, [currentMode]);

  // Fetch flashcard data
  useEffect(() => {
    const fetchFlashcardData = async () => {
      // Get user session
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;

      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        // First, try to find the module by position from the global state
        let currentMod = modules?.find(m => String(m.id) === String(id));

        // If not found in global state (e.g., on refresh), fetch it directly from Supabase by ID
        if (!currentMod) {
          // Check if id is a UUID (contains dashes) or a position (number)
          const isUUID = typeof id === 'string' && id.includes('-');
          
          let query = supabase
            .from('modules_new')
            .select('*');
          
          if (isUUID) {
            query = query.eq('id', id);
          } else {
            query = query.eq('position', Number(id))
                        .eq('user_id', userId);
          }
          
          const { data: moduleData, error: moduleError } = await query.single();

          if (moduleError) {
            console.error('Error fetching module:', moduleError);
            setLoading(false);
            return;
          }

          currentMod = moduleData;
        }

        if (!currentMod) {
          setLoading(false);
          return;
        }

        // Check access for classroom modules
        if (currentMod.classroom_id) {
          const { checkModuleAccess } = await import('../utils/checkModuleAccess');
          const accessCheck = await checkModuleAccess(currentMod.id, currentMod.classroom_id);
          if (!accessCheck.canAccess) {
            setError(accessCheck.error || 'Access to this module is blocked');
            setLoading(false);
            return;
          }
        }

        setModuleData(currentMod);

        // Fetch word group by ID directly (groupId is now the UUID)
        const { data: groupData, error: groupError } = await supabase
          .from('word_groups')
          .select('*')
          .eq('module_id', currentMod.id)
          .eq('id', groupId)
          .single();

        if (groupError) {
          console.error('Error fetching word group:', groupError);
          setLoading(false);
          return;
        }

        setGroupData(groupData);

        // Fetch words for this group with user's SRS data
        const { data: wordsData, error: wordsError } = await supabase
          .from('words')
          .select(`
            id,
            group_id,
            from_text,
            to_text,
            position,
            created_at,
            updated_at,
            word_srs_data!left (
              sm2_interval,
              learning_interval,
              sm2_ease,
              learning_ease,
              next_due,
              apparent_tier,
              history,
              updated_at
            )
          `)
          .eq('group_id', groupData.id)
          .eq('word_srs_data.user_id', userId)
          .order('position', { ascending: true });

        if (wordsError) {
          console.error('Error fetching words:', wordsError);
          setLoading(false);
          return;
        }

        // Merge word data with SRS data
        const mergedWords = (wordsData || []).map(word => {
          const srs = word.word_srs_data?.[0] || null;
          const sm2Ease = srs?.sm2_ease ?? 2.5;
          const learningEase = srs?.learning_ease ?? 2.5;

          return {
            ...word,
            word_srs_data: undefined,
            sm2_interval: srs?.sm2_interval ?? 0,
            learning_interval: srs?.learning_interval ?? 0,
            sm2_ease: sm2Ease,
            learning_ease: learningEase,
            // Keep legacy ease alias mapped from SM2 ease for any callers that still read it
            ease: sm2Ease,
            next_due: srs?.next_due ?? 0,
            apparent_tier: srs?.apparent_tier ?? 1,
            history: srs?.history 
              ? (typeof srs.history === 'string' ? JSON.parse(srs.history) : srs.history)
              : [],
            srs_updated_at: srs?.updated_at || word.updated_at
          };
        });

        setWords(mergedWords);
      } catch (error) {
        console.error('Error loading flashcard data:', error);
      }

      setLoading(false);
    };

    fetchFlashcardData();
  }, [id, groupId, modules, navigate, mode]);

  // Handle case where module/group is missing
  useEffect(() => {
    if (loading) return;

    if (!moduleData || !groupData) {
      // Module or group not found, redirect back
      navigate('/dashboard');
      return;
    }
  }, [loading, moduleData, groupData, navigate, mode]);

  useEffect(() => {
    if (!groupData) return;

    // Use groupWords from state if provided, otherwise use fetched words
    const wordsToUse = groupWords || words || [];

    // Default to classic mode if no mode is specified
    const currentMode = mode || 'classic';

    // For SM2 mode, filter to only due words
    const loadDueWords = async () => {
      // Get user session for SM2 mode check
      const { data: { session } } = await supabase.auth.getSession();
      const currentUserId = session?.user?.id;

      if (currentMode !== 'sm2' || !currentUserId) {
        // For non-SM2 modes, ensure words are sorted by position
        const sortedWords = [...wordsToUse].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        setActiveWords(sortedWords);
        setLoadingSRS(false);
        return;
      }

      // For SM2 mode, fetch fresh data from Supabase with SRS data and filter due words
      try {
        // Fetch fresh words data with SRS from word_srs_data table
        const { data: freshWordsData, error: wordsError } = await supabase
          .from('words')
          .select(`
            id,
            group_id,
            from_text,
            to_text,
            position,
            word_srs_data!left (
              sm2_interval,
              learning_interval,
              sm2_ease,
              learning_ease,
              next_due,
              apparent_tier,
              history,
              updated_at
            )
          `)
          .eq('group_id', groupData.id)
          .eq('word_srs_data.user_id', currentUserId)
          .order('position', { ascending: true });

        if (wordsError) {
          console.error('Error fetching fresh words for SM2:', wordsError);
          // Fallback to initial data
          const sortedWords = [...wordsToUse].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
          setActiveWords(sortedWords);
          setLoadingSRS(false);
          return;
        }

        // Merge SRS data and filter due words
        const now = Date.now();
        const mergedWords = (freshWordsData || []).map(word => {
          const srs = word.word_srs_data?.[0] || null;
          return {
            ...word,
            word_srs_data: undefined,
            sm2_interval: srs?.sm2_interval ?? 0,
            learning_interval: srs?.learning_interval ?? 0,
            ease: srs?.sm2_ease ?? 2.5,
            next_due: srs?.next_due ?? 0,
            apparent_tier: srs?.apparent_tier ?? 1,
            history: srs?.history 
              ? (typeof srs.history === 'string' ? JSON.parse(srs.history) : srs.history)
              : []
          };
        });

        const dueWords = mergedWords.filter(word => {
          const nextDue = word.next_due ?? 0;
          const isDue = nextDue === 0 || nextDue <= now;
          return isDue;
        });

        // Sort due words by when they are due (earliest first)
        // Use position as a secondary sort key for deterministic ordering when next_due values are equal
        const sortedDueWords = dueWords.sort((a, b) => {
          const aDue = a.next_due ?? 0;
          const bDue = b.next_due ?? 0;
          // Primary sort: by due date (earliest first)
          if (aDue !== bDue) {
            return aDue - bDue;
          }
          // Secondary sort: by position when next_due values are equal
          return (a.position ?? 0) - (b.position ?? 0);
        });
        setActiveWords(sortedDueWords);
        setLoadingSRS(false);
      } catch (error) {
        console.error('Error in SM2 due word filtering:', error);
        // Fallback to initial data
        const sortedWords = [...wordsToUse].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        setActiveWords(sortedWords);
        setLoadingSRS(false);
      }
    };

    loadDueWords();
    setLearningMode(useLearningUI);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, groupWords, useLearningUI, words, groupData, navigate, moduleData]);

  const handleNextCard = useCallback(() => {
    if (activeWords.length === 0) return;
    setCurrentCardIndex((prevIndex) => (prevIndex + 1) % activeWords.length);
    setIsFlipped(false);
  }, [activeWords.length]);

  const handlePrevCard = useCallback(() => {
    if (activeWords.length === 0) return;
    setCurrentCardIndex((prevIndex) => (prevIndex - 1 + activeWords.length) % activeWords.length);
    setIsFlipped(false);
  }, [activeWords.length]);

  const handleFlipCard = useCallback(() => {
    setIsFlipped(prev => !prev);
  }, []);

  useEffect(() => {
    // Only apply aggressive protection for learning mode and SM2 mode
    if (learningMode || mode === 'sm2') {
      let isProcessing = false;
      let lastKeyTime = 0;
      const DEBOUNCE_TIME = 300; // Increased debounce time further
      let processingTimeout = null;

      const handleKeyDown = (e) => {
        // Block ALL key events if we're processing
        if (isProcessing) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        // Prevent key repeat events (when holding down a key)
        if (e.repeat) {
          e.preventDefault();
          return;
        }

        // Prevent rapid key presses with time-based debouncing
        const now = Date.now();
        if ((now - lastKeyTime) < DEBOUNCE_TIME) {
          e.preventDefault();
          return;
        }

        // Set processing flag immediately
        isProcessing = true;
        lastKeyTime = now;

        // Clear any existing timeout
        if (processingTimeout) {
          clearTimeout(processingTimeout);
        }

        try {
          if (e.code === 'Space') {
            // Only handle Space globally if it's not on a flashcard element
            const flashcardElement = e.target.closest('.flashcard');
            if (!flashcardElement) {
              e.preventDefault();
              handleFlipCard();
            }
          }
        } catch (error) {
          console.error('Error in key handler:', error);
        }

        // Set timeout to clear processing flag
        processingTimeout = setTimeout(() => {
          isProcessing = false;
        }, DEBOUNCE_TIME);
      };

      window.addEventListener('keydown', handleKeyDown, true); // Use capture phase
      return () => {
        window.removeEventListener('keydown', handleKeyDown, true);
        if (processingTimeout) {
          clearTimeout(processingTimeout);
        }
      };
    } else {
      // For normal mode, use simple protection without aggressive debouncing
      const handleKeyDown = (e) => {
        if (e.code === 'Space') {
          // Only handle Space globally if it's not on a flashcard element
          const flashcardElement = e.target.closest('.flashcard');
          if (!flashcardElement) {
            e.preventDefault();
            handleFlipCard();
          }
        } else if (e.key === 'ArrowRight') {
          // Right arrow behavior depends on language direction
          if (isRTL) {
            handlePrevCard();
          } else {
            handleNextCard();
          }
        } else if (e.key === 'ArrowLeft') {
          // Left arrow behavior depends on language direction
          if (isRTL) {
            handleNextCard();
          } else {
            handlePrevCard();
          }
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [learningMode, mode, handleFlipCard, handleNextCard, handlePrevCard, isRTL]);

  // Learning mode keyboard handling
  useEffect(() => {
    if (learningMode) {
      let isProcessing = false;
      let lastKeyTime = 0;
      const DEBOUNCE_TIME = 300; // Increased debounce time further
      let processingTimeout = null;

      const handleLearningKeyDown = (e) => {
        // Block ALL key events if we're processing
        if (isProcessing) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        // Prevent key repeat events (when holding down a key)
        if (e.repeat) {
          e.preventDefault();
          return;
        }

        // Prevent rapid key presses with time-based debouncing
        const now = Date.now();
        if ((now - lastKeyTime) < DEBOUNCE_TIME) {
          e.preventDefault();
          return;
        }

        // Set processing flag immediately
        isProcessing = true;
        lastKeyTime = now;

        // Clear any existing timeout
        if (processingTimeout) {
          clearTimeout(processingTimeout);
        }

        try {
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            // Right arrow behavior depends on language direction
            if (isRTL) {
              // In RTL: Right arrow = X (didn't know it)
              const xButton = document.querySelector('.flashcard-feedback-btn.x');
              if (xButton) {
                xButton.click();
              }
            } else {
              // In LTR: Right arrow = check (knew it)
              const checkButton = document.querySelector('.flashcard-feedback-btn.check');
              if (checkButton) {
                checkButton.click();
              }
            }
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            // Left arrow behavior depends on language direction
            if (isRTL) {
              // In RTL: Left arrow = check (knew it)
              const checkButton = document.querySelector('.flashcard-feedback-btn.check');
              if (checkButton) {
                checkButton.click();
              }
            } else {
              // In LTR: Left arrow = X (didn't know it)
              const xButton = document.querySelector('.flashcard-feedback-btn.x');
              if (xButton) {
                xButton.click();
              }
            }
          }
        } catch (error) {
          console.error('Error in learning key handler:', error);
        }

        // Set timeout to clear processing flag
        processingTimeout = setTimeout(() => {
          isProcessing = false;
        }, DEBOUNCE_TIME);
      };

      window.addEventListener('keydown', handleLearningKeyDown, true); // Use capture phase
      return () => {
        window.removeEventListener('keydown', handleLearningKeyDown, true);
        if (processingTimeout) {
          clearTimeout(processingTimeout);
        }
      };
    }
  }, [learningMode, isRTL]);

  const preloadSRSData = useCallback(async (words) => {
    // Get user session for SRS data preloading
    const { data: { session } } = await supabase.auth.getSession();
    const currentUserId = session?.user?.id;

    if (!currentUserId || !words || words.length === 0 || !moduleData || !groupData) return {};
    const currentRequestId = ++requestIdRef.current;
    try {
      // Refetch words with SRS data from word_srs_data table
        const { data: freshWordsData, error: wordsError } = await supabase
        .from('words')
        .select(`
          id,
          group_id,
          from_text,
          to_text,
          position,
          word_srs_data!left (
            sm2_interval,
            learning_interval,
            sm2_ease,
            learning_ease,
            next_due,
            apparent_tier,
            history,
            updated_at
          )
        `)
        .eq('group_id', groupData.id)
        .eq('word_srs_data.user_id', currentUserId)
        .order('position', { ascending: true });

      if (wordsError) {
        console.error('Error refetching words for SRS data:', wordsError);
        return {};
      }

      // Convert word data to the format expected by Flashcard component / SRS hook
      const formattedSRSData = {};
      freshWordsData.forEach(word => {
        const groupId = getGroupId(moduleData, groupData.id);
        const key = getWordKey(word, moduleData.id, groupId);
        const srs = word.word_srs_data?.[0] || null;

        // Use SRS data from word_srs_data table
        const sm2Ease = srs?.sm2_ease ?? 2.5;
        const learningEase = srs?.learning_ease ?? 2.5;

        formattedSRSData[key] = {
          sm2_interval: srs?.sm2_interval ?? 0,
          learning_interval: srs?.learning_interval ?? 0,
          sm2_ease: sm2Ease,
          learning_ease: learningEase,
          // Keep legacy ease alias mapped from SM2 ease for any callers that still read it
          ease: sm2Ease,
          nextDue: srs?.next_due ?? 0,
          history: srs?.history 
            ? (typeof srs.history === 'string' ? JSON.parse(srs.history) : srs.history)
            : [],
          apparentTier: srs?.apparent_tier ?? 1,
          lastUpdated: srs?.updated_at 
            ? new Date(srs.updated_at).getTime() 
            : Date.now()
        };
      });

      // Only update if this is the latest request
      if (requestIdRef.current === currentRequestId) {
        setPreloadedSRSData(formattedSRSData);
        setSrsDataReady(true); // Mark SRS data as ready
        setLoadingSRS(false); // Stop loading for SM2 mode
      }
      return formattedSRSData;
    } catch (error) {
      if (requestIdRef.current === currentRequestId) {
        console.error('Error preloading SRS data:', error);
        setPreloadedSRSData({});
        setSrsDataReady(false); // Reset SRS data ready state on error
      }
      return {};
    }
  }, [moduleData, groupData]);

  useEffect(() => {
    if (activeWords.length > 0 && moduleData && groupData) {
      setSrsDataReady(false); // Reset SRS data ready state before loading
      preloadSRSData(activeWords);
    }
  }, [activeWords, moduleData, groupData, preloadSRSData, loading]);





  const handleShuffle = () => {
    setActiveWords(prevWords => {
      const shuffled = [...prevWords];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    });
    setCurrentCardIndex(0);
    setIsFlipped(false);
  };

  const getOptimalWeight = useCallback((srs) => {
    const now = Date.now();
    const isDue = srs.nextDue <= now;
    const recentMistakes = srs.history.slice(-3).filter(h => !h.result).length;
    const interval = useLearningData ? srs.learning_interval ?? 0 : srs.sm2_interval ?? 0; let weight = 1;

    // Due words get highest priority (4x weight)
    if (isDue) weight *= 4;

    // Recent mistakes get priority (2x weight)
    if (recentMistakes > 0) weight *= 2;

    // New words (low interval) get priority (1.5x weight)
    if (interval < 2) weight *= 1.5;

    // Very well known words (high interval) get reduced priority (0.3x weight)
    if (interval > 10) weight *= 0.3;

    return weight;
  }, [useLearningData]);

  function weightedRandomIndex(weights) {
    const total = weights.reduce((a, b) => a + b, 0);
    let r = Math.random() * total;
    for (let i = 0; i < weights.length; i++) {
      if (r < weights[i]) return i;
      r -= weights[i];
    }
    return weights.length - 1;
  }

  // Callback to update preloadedSRSData when Flashcard provides feedback
  const handleSRSUpdate = useCallback((wordKey, updatedSRSData, mode = 'learning') => {
    setPreloadedSRSData(prev => ({
      ...prev,
      [wordKey]: updatedSRSData
    }));

    // Note: We don't re-filter due words here to prevent card flashing
    // The due word filtering happens on page load and when SRS data is preloaded
  }, []);

  const handleSm2RegisterUndo = useCallback((payload) => {
    setSm2LastUndo(payload);
    setSm2UndoError('');
  }, []);

  const handleSm2GoBack = useCallback(async () => {
    if (!sm2LastUndo || !session?.user?.id) return;
    const { wordKey, wordId, previousSrs, cardIndex } = sm2LastUndo;
    setSm2UndoError('');
    try {
      await persistSm2SrsStateToSupabase(wordId, session.user.id, previousSrs);
      const preloaded = {
        ...previousSrs,
        ease: previousSrs.sm2_ease ?? 2.5
      };
      const wf = srsSnapshotToWordFields(previousSrs);
      setPreloadedSRSData(prev => ({ ...prev, [wordKey]: preloaded }));
      setActiveWords(prev =>
        prev.map(w =>
          w.id === wordId
            ? {
                ...w,
                ...wf
              }
            : w
        )
      );
      setCurrentCardIndex(cardIndex);
      setIsFlipped(false);
      setSm2LastUndo(null);
    } catch (err) {
      console.error('SM2 undo failed:', err);
      setSm2UndoError(t('errorUpdatingProgress') || 'Failed to update your progress. Please try again.');
    }
  }, [sm2LastUndo, session?.user?.id, t]);

  const handleLearningRegisterUndo = useCallback((payload) => {
    setLearningLastUndo(payload);
    setLearningUndoError('');
  }, []);

  const handleLearningGoBack = useCallback(async () => {
    if (!learningLastUndo || !session?.user?.id) return;
    const { wordKey, wordId, previousSrs, cardIndex } = learningLastUndo;
    setLearningUndoError('');
    try {
      await persistSm2SrsStateToSupabase(wordId, session.user.id, previousSrs);
      const preloaded = {
        ...previousSrs,
        ease: previousSrs.sm2_ease ?? 2.5
      };
      const wf = srsSnapshotToWordFields(previousSrs);
      setPreloadedSRSData(prev => ({ ...prev, [wordKey]: preloaded }));
      setActiveWords(prev =>
        prev.map(w =>
          w.id === wordId
            ? {
                ...w,
                ...wf
              }
            : w
        )
      );
      if (typeof cardIndex === 'number') {
        setCurrentCardIndex(cardIndex);
      }
      setIsFlipped(false);
      setLearningCardEpoch(e => e + 1);
      setLearningLastUndo(null);
    } catch (err) {
      console.error('Learning undo failed:', err);
      setLearningUndoError(t('errorUpdatingProgress') || 'Failed to update your progress. Please try again.');
    }
  }, [learningLastUndo, session?.user?.id, t]);

  // Reset initial card selection when mode or group changes
  useEffect(() => {
    initialCardSelectedRef.current = false;
  }, [mode, moduleData?.id, groupData?.id]);

  // When learning mode is activated, start with a weighted random card
  useEffect(() => {
    const currentMode = mode || 'classic';
    if (currentMode === 'learning' && activeWords.length > 0 && !initialCardSelectedRef.current) {
      // Calculate weights for all words based on their SRS data
      const weights = activeWords.map(word => {
        const groupId = getGroupId(moduleData, groupData.id);
        const key = getWordKey(word, moduleData.id, groupId);
        const srs = preloadedSRSData?.[key] || {
          sm2_interval: word.sm2_interval ?? 0,
          learning_interval: word.learning_interval ?? 0,
          ease: word.sm2_ease ?? 2.5,
          nextDue: word.next_due ?? 0,
          history: typeof word.history === 'string' ? JSON.parse(word.history) : (word.history || []),
          apparentTier: word.apparent_tier ?? 1,
          lastUpdated: word.updated_at ? new Date(word.updated_at).getTime() : Date.now()
        };
        return getOptimalWeight(srs);
      });

      // Select initial card using weighted random
      const initialIndex = weightedRandomIndex(weights);
      setCurrentCardIndex(initialIndex);
      setIsFlipped(false);
      initialCardSelectedRef.current = true;
    }
  }, [learningMode, activeWords, mode, moduleData, groupData, preloadedSRSData, getOptimalWeight]);

  // Show loading if module/group not found
  if (!moduleData || !groupData) {
    return (
      <div className="flashcards-page-container">
        <div className="flashcard-view-container">
          <div className="flashcard-skeleton" style={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: '#aaa' }}>
            {t('loading')}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
        <h2>{t('accessDenied') || 'Access Denied'}</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          {t('backToDashboard') || 'Back to Dashboard'}
        </button>
      </div>
    );
  }

  if (loadingSRS) {
    return (
      <div className="flashcards-page-container">
        <div className="flashcard-view-container">
          <div className="flashcard-skeleton" style={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: '#aaa' }}>
            {t('loading')}
          </div>
        </div>
      </div>
    );
  }

  const currentWord = activeWords[currentCardIndex];

  const noDueWords = currentMode === 'sm2' && activeWords.length === currentCardIndex;

  if (noDueWords) {
    return (
      <div className="flashcards-page-container">
        <div className="flashcard-view-container">
          <div className="flashcard-skeleton" style={{
            height: 320,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            color: '#aaa'
          }}>
            {t('noDueWords') || 'No due words.'}
          </div>
          <button
            onClick={() => navigate(`/module/${moduleData?.id || id}/group/${groupData.id}`)}
            className="btn btn-primary"
            style={{ marginTop: '1rem' }}
          >
            {t('returnToWordGroup') || 'Return to Word Group'}
          </button>
          {sm2LastUndo ? (
            <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
              <button
                type="button"
                onClick={handleSm2GoBack}
                className="btn btn-secondary"
                style={{
                  fontSize: '0.8125rem',
                  opacity: 0.75,
                  padding: '0.35rem 0.75rem'
                }}
              >
                {isRTL ? '→' : '←'} {t('sm2UndoLastCard')}
              </button>
              {sm2UndoError ? (
                <div style={{ color: 'red', marginTop: '0.5rem', fontSize: '0.85rem' }}>{sm2UndoError}</div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  // In learning mode, advance endlessly, no prev/next, no card count
  const handleLearningNext = () => {
    // In learning mode, use weighted random selection based on SRS data
    if (activeWords.length === 0) return;

    // Filter out the current word to prevent reselecting it
    const availableWords = activeWords.filter((word, index) => index !== currentCardIndex);

    if (availableWords.length === 0) return; // Fallback if only one word

    // Calculate weights for available words based on their SRS data
    const weights = availableWords.map(word => {
      const groupId = getGroupId(moduleData, groupData.id);
      const key = getWordKey(word, moduleData.id, groupId);
      const srs = preloadedSRSData?.[key] || {
        sm2_interval: word.sm2_interval ?? 0,
        learning_interval: word.learning_interval ?? 0,
        ease: word.sm2_ease ?? 2.5,
        nextDue: word.next_due ?? 0,
        history: typeof word.history === 'string' ? JSON.parse(word.history) : (word.history || []),
        apparentTier: word.apparent_tier ?? 1,
        lastUpdated: word.updated_at ? new Date(word.updated_at).getTime() : Date.now()
      };
      return getOptimalWeight(srs);
    });

    // Select next card using weighted random from available words
    const nextIndex = weightedRandomIndex(weights);
    // Map back to original activeWords index
    const originalIndex = activeWords.findIndex((word, index) =>
      availableWords[nextIndex] === word
    );
    setCurrentCardIndex(originalIndex);
    setIsFlipped(false);
  };

  const handleSm2Next = () => {
    setCurrentCardIndex(prev => prev + 1);
    setIsFlipped(false);
  };


  return (
    <div className="flashcards-page-container">
      <button onClick={() => navigate(`/module/${moduleData.id}/group/${groupData.id}`)} className="btn btn-secondary btn-back">
        {isRTL ? '→' : '←'} {t('exitFlashcards')}
      </button>
      <div className="flashcard-view-container">
        {loading || !srsDataReady ? (
          <div className="flashcard-skeleton" style={{
            height: 320,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            color: '#aaa'
          }}>
            {t('loading') || 'Loading...'}
          </div>
        ) : (
          <>
            <Flashcard
              key={`${currentMode === 'learning' ? `${learningCardEpoch}-` : ''}${currentCardIndex}-${currentWord?.id || ''}`}
              word={currentWord}
              module={moduleData}
              isFlipped={isFlipped}
              onFlip={handleFlipCard}
              moduleId={moduleData.id}
              groupIdx={groupData.id}
              onFeedback={currentMode === 'sm2' ? handleSm2Next : learningMode ? handleLearningNext : handleNextCard}
              onSRSUpdate={(key, updated) => handleSRSUpdate(key, updated, 'learning')}
              userId={session?.user?.id}
              preloadedSRSData={preloadedSRSData}
              mode={currentMode === 'sm2' ? 'sm2' : 'learning'}
              sm2UndoCardIndex={currentMode === 'sm2' ? currentCardIndex : undefined}
              onSm2RegisterUndo={currentMode === 'sm2' ? handleSm2RegisterUndo : undefined}
              learningUndoCardIndex={currentMode === 'learning' ? currentCardIndex : undefined}
              onLearningRegisterUndo={currentMode === 'learning' ? handleLearningRegisterUndo : undefined}
            />
          </>
        )}
        {!loading && srsDataReady && !learningMode && (
          <>
            <div className="flashcard-nav">
              <button onClick={handlePrevCard} className="btn btn-secondary">
                {isRTL ? '→' : '←'} {t('prev')}
              </button>
              <span>{isRTL ? `${activeWords.length} / ${currentCardIndex + 1}` : `${currentCardIndex + 1} / ${activeWords.length}`}</span>
              <button onClick={handleNextCard} className="btn btn-secondary">
                {t('next')} {isRTL ? '←' : '→'}
              </button>
            </div>
            <button onClick={handleShuffle} className="btn btn-secondary btn-shuffle">{t('shuffleCards')}</button>
            <div className="flashcard-hotkeys" style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
              {t('flashcardHotkeys').replace('{arrows}', isRTL ? '→/←' : '←/→')}
            </div>
          </>
        )}
        {!loading && srsDataReady && currentMode === 'sm2' ? (
          <>
            {activeWords.length > 0 && currentCardIndex < activeWords.length && (
              <div className="due-counter" style={{
                fontWeight: 'bold',
                fontSize: '1rem',
                color: '#555',
                textAlign: 'center',
                direction: isRTL ? 'rtl' : 'ltr'
              }}>
                {t('cardsLeft') || 'Cards Left:'} {activeWords.length - currentCardIndex}
              </div>
            )}
            {sm2LastUndo ? (
              <div
                className="sm2-undo-active"
                style={{
                  marginTop: '0.75rem',
                  textAlign: 'center',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}
              >
                <button
                  type="button"
                  onClick={handleSm2GoBack}
                  className="btn btn-secondary"
                  style={{
                    width: '100%',
                    maxWidth: '440px',
                    fontWeight: 600,
                    fontSize: '1rem',
                    padding: '0.7rem 1rem'
                  }}
                >
                  {isRTL ? '→' : '←'} {t('sm2UndoLastCard')}
                </button>
                {sm2UndoError ? (
                  <div style={{ color: 'red', marginTop: '0.5rem', fontSize: '0.9rem' }}>{sm2UndoError}</div>
                ) : null}
              </div>
            ) : null}
            <div
              className="sm2-mode-info"
              style={{
                marginTop: (sm2LastUndo || (activeWords.length > 0 && currentCardIndex < activeWords.length)) ? '0.75rem' : 0,
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: 'center'
              }}
            >
              {t('sm2ModeInfo') || 'These are your due words. Review each one once.'}
            </div>
          </>
        ) : !loading && srsDataReady && learningMode && currentMode === 'learning' ? (
          <>
            {learningLastUndo ? (
              <div
                className="learning-undo-active"
                style={{
                  marginTop: '0.75rem',
                  textAlign: 'center',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}
              >
                <button
                  type="button"
                  onClick={handleLearningGoBack}
                  className="btn btn-secondary"
                  style={{
                    width: '100%',
                    maxWidth: '440px',
                    fontWeight: 600,
                    fontSize: '1rem',
                    padding: '0.7rem 1rem'
                  }}
                >
                  {isRTL ? '→' : '←'} {t('sm2UndoLastCard')}
                </button>
                {learningUndoError ? (
                  <div style={{ color: 'red', marginTop: '0.5rem', fontSize: '0.9rem' }}>{learningUndoError}</div>
                ) : null}
              </div>
            ) : null}
            <div
              className="learning-mode-info"
              style={{
                marginTop: learningLastUndo ? '0.75rem' : 0,
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: 'center'
              }}
            >
              {t('learningModeInfo')}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default FlashcardsPage; 